<template>
  <div class="nd-overlay" @click.self="$emit('fechar')">
    <div class="nd-modal">

      <!-- Cabeçalho -->
      <div class="nd-modal__header">
        <div>
          <h4 class="nd-modal__title">
            <i class="fas fa-file-invoice mr-2"></i>
            NFC-e #{{ nfceDetalhe.numero || nfceDetalhe.numeroNota || '—' }} / Série {{ nfceDetalhe.serie || '—' }}
          </h4>
          <small class="text-muted">{{ nfceDetalhe.uuid_venda_terminal }}</small>
        </div>
        <button class="nd-btn nd-btn--light" @click="$emit('fechar')">✕</button>
      </div>

      <!-- Corpo com abas (resumo + itens) -->
      <div class="nd-tabs">
        <button
          :class="['nd-tab', { 'nd-tab--active': abaAtiva === 'resumo' }]"
          @click="abaAtiva = 'resumo'"
        >Resumo</button>
        <button
          :class="['nd-tab', { 'nd-tab--active': abaAtiva === 'itens' }]"
          @click="abaAtiva = 'itens'"
        >Itens</button>
      </div>

      <div class="nd-modal__body">

        <!-- ── RESUMO ────────────────────────────────────────────────── -->
        <template v-if="abaAtiva === 'resumo'">
          <div :class="['nd-status-box', statusClass]">
            <i :class="statusIcon" class="mr-2"></i>
            <strong>{{ statusLabel }}</strong>
            <span class="ml-2 nd-tipo-emissao">{{ tipoEmissaoLabel }}</span>
          </div>

          <table class="nd-info-table">
            <tbody>
              <tr><th>Número</th><td>{{ nfceDetalhe.numero || nfceDetalhe.numeroNota || '—' }}</td></tr>
              <tr><th>Série</th><td>{{ nfceDetalhe.serie || '—' }}</td></tr>
              <tr><th>Emissão</th><td>{{ (nfceDetalhe.emissao || nfceDetalhe.dtVenda || nfceDetalhe.created_at) | dataHora }}</td></tr>
              <tr><th>Cliente</th><td>{{ nfceDetalhe.nomeCliente || 'Consumidor final' }}</td></tr>
              <tr><th>Valor total</th><td><strong>{{ nfceDetalhe.valorTotal | brl }}</strong></td></tr>
              <tr><th>Status</th><td>{{ statusLabel }}</td></tr>
              <tr><th>Tipo emissão</th><td>{{ tipoEmissaoLabel }}</td></tr>
              <tr v-if="nfceDetalhe.protocolo"><th>Protocolo</th><td class="font-mono">{{ nfceDetalhe.protocolo }}</td></tr>
            </tbody>
          </table>

          <!-- Chave de acesso -->
          <div v-if="nfceDetalhe.chave" class="nd-chave-box">
            <div class="nd-chave-label">Chave de acesso</div>
            <div class="nd-chave-valor font-mono">{{ fmtChave(nfceDetalhe.chave) }}</div>
            <button class="nd-btn nd-btn--outline nd-btn--sm mt-2" @click="copiar(nfceDetalhe.chave, 'chave')">
              <i class="fas fa-copy mr-1"></i>{{ copiado === 'chave' ? 'Copiado!' : 'Copiar chave' }}
            </button>
          </div>

          <!-- Formas de pagamento -->
          <div class="nd-secao">
            <h5 class="nd-secao__title">Formas de pagamento</h5>
            <table v-if="pagamentos.length" class="nd-table">
              <thead>
                <tr>
                  <th>Forma</th>
                  <th class="text-right">Parcelas</th>
                  <th class="text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, i) in pagamentos" :key="i">
                  <td>{{ p.descricao || '—' }}</td>
                  <td class="text-right">{{ p.parcelas || 1 }}</td>
                  <td class="text-right">{{ p.valor | brl }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="text-muted small">Sem pagamentos registrados.</div>
          </div>

          <!-- Erro / rejeição -->
          <div
            v-if="nfceDetalhe.motivo_erro || nfceDetalhe.ultimo_erro"
            class="nd-alert nd-alert--danger mt-3"
          >
            <strong>Motivo:</strong> {{ nfceDetalhe.motivo_erro || nfceDetalhe.ultimo_erro }}
            <span v-if="nfceDetalhe.codigo_rejeicao"> (#{{ nfceDetalhe.codigo_rejeicao }})</span>
          </div>
        </template>

        <!-- ── ITENS ─────────────────────────────────────────────────── -->
        <template v-if="abaAtiva === 'itens'">
          <div v-if="carregandoItens" class="text-center py-4 text-muted">
            <span class="spinner-border spinner-border-sm mr-2"></span>Carregando...
          </div>
          <table v-else class="nd-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Produto</th>
                <th class="text-right">Qtd</th>
                <th class="text-right">Unit.</th>
                <th class="text-right">Desc.</th>
                <th class="text-right">Acrésc.</th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="itens.length === 0">
                <td colspan="7" class="text-center text-muted py-3">Nenhum item.</td>
              </tr>
              <template v-for="(item, i) in itens">
                <tr :key="`item-${item.id}`">
                  <td>{{ i + 1 }}</td>
                  <td>{{ item.descricao }}</td>
                  <td class="text-right">{{ formatQtd(item.quantidade) }}</td>
                  <td class="text-right">{{ item.valor_unitario | brl }}</td>
                  <td class="text-right">{{ item.desconto | brl }}</td>
                  <td class="text-right">{{ item.acrescimo | brl }}</td>
                  <td class="text-right"><strong>{{ item.total | brl }}</strong></td>
                </tr>
                <tr
                  v-if="itemExtras(item).length"
                  :key="`extras-${item.id}`"
                  class="nd-item-extras"
                >
                  <td></td>
                  <td colspan="6">
                    <span
                      v-for="(ex, ix) in itemExtras(item)"
                      :key="ix"
                      class="nd-tag"
                    >{{ ex }}</span>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </template>

      </div>

      <!-- Rodapé -->
      <div class="nd-modal__footer">
        <button class="nd-btn nd-btn--secondary" @click="$emit('fechar')">Fechar</button>
      </div>

    </div>
  </div>
</template>

<script>
import { getElectronAPI } from "@/plugins/desktop";

const STATUS_MAP = {
  pendente_xml:                 { label: "Pendente XML",        cls: "nd-status--warning", icon: "fas fa-clock" },
  xml_gerado:                   { label: "XML gerado",          cls: "nd-status--info",    icon: "fas fa-file-code" },
  xml_assinado:                 { label: "XML assinado",        cls: "nd-status--info",    icon: "fas fa-signature" },
  pendente_transmissao_backend: { label: "Pendente transmissão",cls: "nd-status--warning", icon: "fas fa-paper-plane" },
  pendente_transmissao:         { label: "Pendente transmissão",cls: "nd-status--warning", icon: "fas fa-paper-plane" },
  autorizada:                   { label: "Autorizada",          cls: "nd-status--success", icon: "fas fa-check-circle" },
  rejeitada:                    { label: "Rejeitada",           cls: "nd-status--danger",  icon: "fas fa-ban" },
  erro_transmissao:             { label: "Erro transmissão",    cls: "nd-status--danger",  icon: "fas fa-exclamation-circle" },
  erro:                         { label: "Erro",                cls: "nd-status--danger",  icon: "fas fa-exclamation-circle" },
  cancelada:                    { label: "Cancelada",           cls: "nd-status--secondary",icon: "fas fa-ban" },
};

export default {
  name: "NfceDetalheModal",

  filters: {
    dataHora(dt) {
      if (!dt) return "—";
      try {
        return new Date(dt).toLocaleString("pt-BR", {
          day: "2-digit", month: "2-digit", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        });
      } catch { return dt; }
    },
    brl(v) {
      return Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
  },

  props: {
    nfce: { type: Object, required: true },
  },

  emits: ["fechar"],

  data() {
    return {
      abaAtiva:        "resumo",
      itens:           [],
      pagamentos:      [],
      carregandoItens: false,
      copiado:         null,
      nfceFull:        null,
    };
  },

  computed: {
    nfceDetalhe() {
      return this.nfceFull || this.nfce;
    },
    statusLabel() {
      return STATUS_MAP[this.nfceDetalhe.status_fiscal]?.label || this.nfceDetalhe.status_fiscal || "—";
    },
    statusClass() {
      return STATUS_MAP[this.nfceDetalhe.status_fiscal]?.cls || "nd-status--secondary";
    },
    statusIcon() {
      return STATUS_MAP[this.nfceDetalhe.status_fiscal]?.icon || "fas fa-question-circle";
    },
    tipoEmissaoLabel() {
      return this._extrairTpEmis(this.nfceDetalhe) === 9 ? "Contingência" : "Normal";
    },
  },

  async mounted() {
    await this._carregar();
  },

  methods: {
    async _carregar() {
      this.carregandoItens = true;
      try {
        const api  = getElectronAPI();
        const full = await api.nfce.getByUuid(this.nfce.uuid_venda_terminal);
        if (full) {
          this.nfceFull   = full;
          this.itens      = full.itens      || [];
          this.pagamentos = full.pagamentos || [];
        } else {
          this.itens = (await api.nfce.getItens(this.nfce.uuid_venda_terminal)) || [];
        }
      } catch (e) {
        console.warn("[NfceDetalheModal] Erro ao carregar:", e.message);
      } finally {
        this.carregandoItens = false;
      }
    },

    fmtChave(v) {
      if (!v || v.length !== 44) return v || "—";
      return v.match(/.{1,4}/g).join(" ");
    },

    formatQtd(q) {
      const n = Number(q || 0);
      return n % 1 === 0 ? String(n) : n.toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    },

    _parsePayload(item) {
      try { return item.payload_json ? JSON.parse(item.payload_json) : {}; } catch { return {}; }
    },

    itemExtras(item) {
      const extras = [];
      const p = this._parsePayload(item);

      const lote = item.lote || p.lote || p.numero_lote || p.lote_codigo;
      if (lote) extras.push(`Lote: ${lote}`);

      const combinacao = p.combinacao || p.combinacao_descricao || p.combinations;
      if (combinacao) {
        if (Array.isArray(combinacao)) {
          combinacao.forEach((c) => {
            const desc = c?.descricao || c?.nome || c?.label;
            if (desc) extras.push(`Combinação: ${desc}`);
          });
        } else if (typeof combinacao === "string") {
          extras.push(`Combinação: ${combinacao}`);
        }
      }

      const pesado = p.produto_pesado || p.pesado || item.pesado;
      const codigoBalanca = p.codigo_balanca || p.cod_balanca || item.codigo_balanca;
      if (pesado || codigoBalanca) {
        extras.push(`Pesado${codigoBalanca ? ` (cód.: ${codigoBalanca})` : ""}`);
      }

      return extras;
    },

    _extrairTpEmis(nfce) {
      if (!nfce) return 1;
      if (nfce.tipo_emissao != null && String(nfce.tipo_emissao) !== "") return Number(nfce.tipo_emissao);
      if (nfce.tpEmis      != null && String(nfce.tpEmis) !== "")       return Number(nfce.tpEmis);
      try {
        if (nfce.dados_json) {
          const d = typeof nfce.dados_json === "string" ? JSON.parse(nfce.dados_json) : nfce.dados_json;
          const fromJson = d?.ide?.tpEmis ?? d?.tpEmis;
          if (fromJson != null) return Number(fromJson);
        }
      } catch { /* ignora */ }
      const xml = nfce.xml_autorizado || nfce.xml_assinado || nfce.xml;
      if (xml && typeof xml === "string") {
        const m = xml.match(/<tpEmis>\s*(\d)\s*<\/tpEmis>/i);
        if (m) return Number(m[1]);
      }
      return 1;
    },

    copiar(texto, chave = "chave") {
      if (!texto) return;
      navigator.clipboard.writeText(texto).then(() => {
        this.copiado = chave;
        setTimeout(() => { this.copiado = null; }, 2500);
      }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.nd-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.nd-modal {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 40px rgba(0,0,0,.22);
  width: 820px;
  max-width: 96vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.nd-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.1rem 1.5rem .85rem;
  border-bottom: 1px solid #dee2e6;
}
.nd-modal__title { font-size: 1.05rem; font-weight: 700; margin: 0; }

.nd-modal__body {
  padding: 1.1rem 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.nd-modal__footer {
  padding: .85rem 1.5rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
}

.nd-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #dee2e6;
  padding: 0 1.5rem;
}
.nd-tab {
  padding: .55rem 1rem;
  border: none;
  background: transparent;
  font-size: .85rem;
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
}
.nd-tab--active { color: var(--primary-color, #ee7361); border-bottom-color: var(--primary-color, #ee7361); }

/* Status box */
.nd-status-box {
  display: flex;
  align-items: center;
  padding: .7rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: .9rem;
}
.nd-status--success   { background: #d1e7dd; color: #0a3622; }
.nd-status--warning   { background: #fff3cd; color: #856404; }
.nd-status--danger    { background: #f8d7da; color: #58151c; }
.nd-status--info      { background: #cff4fc; color: #055160; }
.nd-status--secondary { background: #e9ecef; color: #495057; }
.nd-tipo-emissao {
  font-weight: 500;
  font-size: .8rem;
  opacity: .85;
}

.nd-info-table { width: 100%; font-size: .87rem; }
.nd-info-table th {
  text-align: left;
  font-weight: 600;
  color: #6c757d;
  padding: .35rem .5rem;
  width: 38%;
}
.nd-info-table td { padding: .35rem .5rem; }

.nd-chave-box {
  margin-top: .85rem;
  padding: .75rem 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}
.nd-chave-label { font-size: .72rem; text-transform: uppercase; color: #6c757d; font-weight: 700; letter-spacing: .04em; }
.nd-chave-valor { font-size: .82rem; word-break: break-all; margin-top: .25rem; }

.nd-secao { margin-top: 1.25rem; }
.nd-secao__title {
  font-size: .82rem;
  text-transform: uppercase;
  font-weight: 700;
  color: #6c757d;
  letter-spacing: .04em;
  margin: 0 0 .5rem 0;
}

.nd-table { width: 100%; border-collapse: collapse; font-size: .85rem; }
.nd-table th {
  font-size: .72rem;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #6c757d;
  background: #f8f9fa;
  padding: .5rem .6rem;
  text-align: left;
  border-bottom: 1px solid #dee2e6;
}
.nd-table th.text-right { text-align: right; }
.nd-table td { padding: .5rem .6rem; border-bottom: 1px solid #f1f3f5; }
.nd-table td.text-right { text-align: right; }

.nd-item-extras td {
  padding-top: 0;
  padding-bottom: .5rem;
  border-bottom: 1px solid #f1f3f5;
}
.nd-tag {
  display: inline-block;
  background: #fde8e5;
  color: var(--primary-color, #ee7361);
  font-size: .7rem;
  padding: 2px 8px;
  border-radius: 10px;
  margin-right: .35rem;
  margin-top: .25rem;
}

.nd-alert {
  padding: .6rem .85rem;
  border-radius: 8px;
  font-size: .85rem;
}
.nd-alert--danger { background: #f8d7da; color: #58151c; }

.nd-btn {
  padding: .45rem .9rem;
  border-radius: 7px;
  border: 1.5px solid transparent;
  cursor: pointer;
  font-size: .85rem;
  font-weight: 600;
  background: #fff;
  color: #495057;
}
.nd-btn--light     { background: #f1f3f5; color: #495057; }
.nd-btn--outline   { background: #fff; color: #495057; border-color: #ced4da; }
.nd-btn--secondary { background: #6c757d; color: #fff; border-color: #6c757d; }
.nd-btn--sm        { padding: .25rem .6rem; font-size: .78rem; }

.font-mono { font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; }
.text-muted { color: #6c757d; }
.text-right { text-align: right; }
.text-center { text-align: center; }
.small { font-size: .8rem; }
.py-3 { padding-top: .85rem; padding-bottom: .85rem; }
.py-4 { padding-top: 1.25rem; padding-bottom: 1.25rem; }
.mr-1 { margin-right: .25rem; }
.mr-2 { margin-right: .5rem; }
.ml-2 { margin-left: .5rem; }
.mt-2 { margin-top: .5rem; }
.mt-3 { margin-top: .85rem; }
</style>
